import { useRef, useState, useEffect, useCallback } from "react"
import { Upload, Image as ImageIcon, Video, X as CloseIcon, Edit2 } from "lucide-react"
import { DirectUpload } from "@rails/activestorage"
import { useDropzone } from 'react-dropzone'
import { toast } from "sonner"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { ScrollArea } from "@/components/ui/scroll-area"

// Helper to get CSRF token
const getCsrfToken = () => {
  const meta = document.querySelector('meta[name="csrf-token"]');
  return meta ? meta.content : null;
}

const MediaUploader = ({ assets = [], onChange, currentSelection, disabled, compactTrigger = false }) => {
  const fileInputRef = useRef(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [isUploading, setIsUploading] = useState(false)
  const [uploadError, setUploadError] = useState(null)
  const [aspectRatio, setAspectRatio] = useState(16/9)

  // Select an existing asset from the gallery
  const handleSelectAsset = (asset) => {
    const simpleMediaType = asset.file_type?.startsWith('image') ? 'image' : asset.file_type?.startsWith('video') ? 'video' : 'unknown';
    onChange({ asset_id: asset.id }, asset.file_url, simpleMediaType)
    setIsModalOpen(false)
  }

  // Clear the selection
  const handleClearMedia = (e) => {
     e.stopPropagation(); // Prevent modal from opening if clicking remove on placeholder
     onChange(null, null, null);
  }

  // Open the modal
  const openModal = () => {
    if (isUploading) return; // Don't open modal during upload
    setIsModalOpen(true);
  }

  // --- Direct Upload Logic (remains the same inside handleDirectUpload) ---
  const directUploadWillStoreFileWithXHR = (request) => {
     const csrfToken = getCsrfToken();
     if (csrfToken) {
       request.setRequestHeader('X-CSRF-Token', csrfToken);
     }
     request.upload.addEventListener("progress", event => directUploadDidProgress(event))
   }

   const directUploadDidProgress = (event) => {
     if (event.lengthComputable) {
       const progress = Math.round((event.loaded / event.total) * 100)
       setUploadProgress(progress)
     }
   }

   const handleDirectUpload = useCallback((file) => {
    setIsUploading(true)
    setUploadProgress(0)
    setUploadError(null)

    const directUploadUrl = `/rails/active_storage/direct_uploads`;
    const upload = new DirectUpload(file, directUploadUrl, {
       directUploadWillStoreFileWithXHR: directUploadWillStoreFileWithXHR,
     });

    upload.create((error, blob) => {
      setIsUploading(false)
      if (error) {
        console.error("Direct upload failed:", error)
        setUploadError(`Upload failed: ${error.message || error}`)
        toast.error(`Upload failed for ${file.name}.`)
      } else {
        const reader = new FileReader();
        const fileType = file.type.startsWith('video/') ? 'video' : 'image';
        reader.onloadend = () => {
          const previewUrl = reader.result;
          onChange({ file: blob.signed_id }, previewUrl, fileType)
          toast.success(`${file.name} uploaded successfully.`);
          setIsModalOpen(false);
        }
        reader.onerror = () => {
          console.error("FileReader error after upload");
          onChange({ file: blob.signed_id }, null, fileType)
          toast.warning(`Uploaded ${file.name}, but couldn't generate preview.`);
          setIsModalOpen(false);
        }
        reader.readAsDataURL(file);
      }
    })
  }, [onChange])

  // Dropzone setup (remains the same for the modal)
  const onDrop = useCallback(acceptedFiles => {
    const file = acceptedFiles[0];
    if (file) {
      handleDirectUpload(file);
    }
  }, [handleDirectUpload])

  const { getRootProps: getDropzoneRootProps, getInputProps: getDropzoneInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp'],
      'video/*': ['.mp4', '.mov', '.avi', '.webm']
    },
    multiple: false,
    disabled: isUploading
  })

  // Determine media type for preview rendering
  const previewMediaType = currentSelection?.mediaType ?? 
  (currentSelection?.url ? (currentSelection.url.match(/\.(mp4|mov|avi|webm)$/i) ? 'video' : 'image') : null);

  // Handle image load to get aspect ratio
  const handleImageLoad = (e) => {
    const { naturalWidth, naturalHeight } = e.target;
    if (naturalWidth && naturalHeight) {
      setAspectRatio(naturalWidth / naturalHeight);
    }
  };

  // Handle video metadata load to get aspect ratio
  const handleVideoLoad = (e) => {
    const { videoWidth, videoHeight } = e.target;
    if (videoWidth && videoHeight) {
      setAspectRatio(videoWidth / videoHeight);
    }
  };

  // Reset aspect ratio when selection changes
  useEffect(() => {
    if (!currentSelection?.url) {
      setAspectRatio(16/9);
    }
  }, [currentSelection?.url]);

  return (
    <div className={currentSelection?.url || isUploading ? "space-y-4" : "contents"}>
      {/* Show preview area only when uploading or has selection */}
      {(isUploading || currentSelection?.url) && (
        <div
          className="group relative w-full rounded-xl border border-border bg-muted/30 overflow-hidden"
          style={{ 
            maxHeight: '400px',
            aspectRatio: aspectRatio,
            maxWidth: aspectRatio > 1 ? '100%' : `${400 * aspectRatio}px`,
            margin: '0 auto'
          }}
        >
          {isUploading && (
            <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-20 rounded-lg">
              <div className="text-white text-center">
                <div>Uploading...</div>
                <div className="w-32 h-2 bg-gray-300 rounded-full overflow-hidden mt-2">
                  <div
                    className="h-full bg-primary transition-all duration-150"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
              </div>
            </div>
          )}

          {!isUploading && currentSelection?.url && (
            <>
              {/* Media Preview */}
              {previewMediaType === 'video' ? (
                <video
                  src={currentSelection.url}
                  controls
                  onLoadedMetadata={handleVideoLoad}
                  className="w-full h-full object-contain bg-black rounded-lg"
                  style={{ maxHeight: '400px' }}
                >
                  Your browser does not support the video tag.
                </video>
              ) : (
                <img
                  src={currentSelection.url}
                  alt="Selected media"
                  onLoad={handleImageLoad}
                  className="object-contain w-full h-full z-0"
                />
              )}

              {/* Overlay Buttons for Existing Media */}
              {!disabled && (
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/30 to-transparent transition-opacity duration-300 flex items-end justify-end p-2 space-x-2 z-10 pointer-events-none">
                <Button
                  size="icon"
                  className="h-8 w-8 pointer-events-auto bg-primary"
                  onClick={openModal}
                  aria-label="Change media"
                >
                  <Edit2 className="h-4 w-4" />
                </Button>
                <Button
                  variant="destructive"
                  size="icon"
                  className="h-8 w-8 pointer-events-auto"
                  onClick={handleClearMedia}
                  aria-label="Remove media"
                >
                  <CloseIcon className="h-4 w-4" />
                </Button>
              </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Simple upload button when no media selected and not uploading */}
      {!isUploading && !currentSelection?.url && (
        <button
          type="button"
          onClick={openModal}
          className={compactTrigger
            ? "flex h-7 w-7 items-center justify-center text-muted-foreground transition-colors hover:text-foreground"
            : "text-muted-foreground hover:text-foreground transition-colors"
          }
          aria-label="Add media"
          title="Add media"
        >
          <ImageIcon className="h-4 w-4" />
        </button>
      )}

      {/* Modal remains the same, but is now controlled by isModalOpen state */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="sm:max-w-[600px] md:max-w-[800px] lg:max-w-[1000px] max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Select or Upload Media</DialogTitle>
            <DialogDescription>
              Choose an existing asset from the content hub or upload a new file.
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 flex flex-col gap-4 overflow-hidden">
            {/* Dropzone Section */}
             <div
                {...getDropzoneRootProps()}
                className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer bg-card hover:bg-muted/50 ${isDragActive ? 'border-primary bg-muted/80' : 'border-muted'} ${isUploading ? 'opacity-50 cursor-not-allowed' : ''} transition-colors`}
             >
                <input {...getDropzoneInputProps()} disabled={isUploading} />
                <div className="flex flex-col items-center justify-center text-center">
                {isUploading ? (
                   <div className="text-center">
                     <div>Uploading...</div>
                      <div className="w-32 h-2 bg-gray-300 rounded-full overflow-hidden mt-2 mx-auto">
                        <div
                          className="h-full bg-primary transition-all duration-150"
                          style={{ width: `${uploadProgress}%` }}
                        />
                      </div>
                   </div>
                 ) : isDragActive ? (
                   <p className="text-sm text-primary"><Upload className="h-6 w-6 mx-auto mb-2" />Drop the file here ...</p>
                 ) : (
                   <>
                     <Upload className="h-8 w-8 mb-2 text-muted-foreground" />
                     <p className="text-sm text-muted-foreground">
                       Drag & drop file here, or click to select file
                     </p>
                   </>
                 )}
               </div>
             </div>
             {uploadError && (
                <p className="text-sm text-destructive text-center">{uploadError}</p>
             )}

             {/* Separator */}
             <div className="relative my-4">
               <div className="absolute inset-0 flex items-center">
                 <span className="w-full border-t" />
               </div>
               <div className="relative flex justify-center text-xs uppercase">
                 <span className="bg-background px-2 text-muted-foreground">
                   Or pick from the content hub
                 </span>
               </div>
             </div>

             {/* Assets Gallery Section */}
             {assets && assets.length > 0 ? (
                 <ScrollArea className="flex-1 pr-3">
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                     {assets.map((asset) => {
                       const isImage = asset.file_type?.startsWith('image');
                       const isVideo = asset.file_type?.startsWith('video');
                       return (
                          <div key={asset.id} className="relative aspect-square w-full rounded-lg border overflow-hidden group focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2">
                           {isImage ? (
                              <img src={asset.file_url} alt={`Asset ${asset.id}`} className="object-cover w-full h-full" />
                            ) : isVideo ? (
                              <video src={`${asset.file_url}#t=0.1`} muted playsInline controls preload="metadata" className="object-cover w-full h-full bg-black"></video>
                            ) : (
                              <div className="w-full h-full bg-muted flex items-center justify-center"><ImageIcon className="w-1/2 h-1/2 text-muted-foreground" /></div>
                            )}
                            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center pointer-events-none">
                              <Button variant="secondary" size="sm" onClick={() => handleSelectAsset(asset)} className="pointer-events-auto">Select</Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                 </ScrollArea>
              ) : (
                 <div className="text-center py-8 text-muted-foreground">No existing media assets found.</div>
              )}
           </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default MediaUploader
